import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjects } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { API_URL, apiRequest } from '../config/api';

function EmissionFactors() {
  const { t } = useTranslation();
  const { selectedProject } = useProjects();
  const { user, handleAuthError } = useAuth();
  
  // Get project ID from selectedProject context
  const projectId = selectedProject?.id;
  
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newFactor, setNewFactor] = useState({
    name: '',
    region: '',
    kgCO2e: '',
    unit: '',
    date: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Predefined options
  const regions = [
    'GLOBAL',
    'US', 'UK', 'PL', 'ARG', 'DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'SE', 'NO', 'DK', 'FI',
    'CA', 'AU', 'JP', 'CN', 'IN', 'BR', 'MX', 'ZA', 'RU', 'TR', 'KR', 'SG', 'MY', 'TH'
  ];

  const units = [
    'KM', 'LITER', 'TKM', 'KWH_TH', 'NONE', 'TONNE', 'KG', 'PIECE', 'KWH'
  ];

  // Empty initial state for emission factors
  const [emissionFactors, setEmissionFactors] = useState([]);

  const fetchEmissionFactors = useCallback(async () => {
    if (!projectId) {
      setError('No project selected');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      if (!user?.token) {
        console.error('No authentication token available');
        setError('Authentication required');
        return;
      }

      const response = await apiRequest(`${API_URL}/api/emission-factors`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      }, handleAuthError);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch emission factors');
      }

      const responseData = await response.json();
      console.log('API Response:', responseData);
      
      // Extract emission factors from pageable response and map (preserve full object for edit)
      const content = Array.isArray(responseData?.content) ? responseData.content : [];
      const mappedFactors = content.map(item => ({
        // fields used in UI
        id: item.id,
        name: item.name,
        region: item.region ?? item.country ?? '',
        kgCO2e: item.coefficient,
        unit: item.emissionUnit, // shown in table
        date: item.validFrom ? String(item.validFrom).slice(0, 10) : '',
        // preserve backend fields for PUT
        activityId: item.activityId,
        description: item.description,
        category: item.category,
        subcategory: item.subcategory,
        activityUnit: item.activityUnit,
        emissionUnit: item.emissionUnit,
        country: item.country,
        source: item.source,
        sourceVersion: item.sourceVersion,
        fuelType: item.fuelType,
        technology: item.technology,
        validFrom: item.validFrom,
        validTo: item.validTo,
        active: item.active,
        version: item.version,
        notes: item.notes,
        uncertaintyPercentage: item.uncertaintyPercentage
      }));
      setEmissionFactors(mappedFactors);
      console.log('Fetched emission factors (mapped):', mappedFactors);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching emission factors:', err);
      // Set empty array on error to prevent slice issues
      setEmissionFactors([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, user?.token, handleAuthError]);

  // Fetch emission factors on component mount
  useEffect(() => {
    if (projectId) {
      fetchEmissionFactors();
    }
  }, [projectId, fetchEmissionFactors]);


  const handleCheckboxChange = (id) => {
    setSelectedRows(prev => 
      prev.includes(id) 
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === (emissionFactors?.length || 0)) {
      setSelectedRows([]);
    } else {
      setSelectedRows((emissionFactors || []).map(item => item.id));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewFactor(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    if (!projectId) {
      setError('No project selected');
      setIsSubmitting(false);
      return;
    }

    if (!user?.token) {
      console.error('No authentication token available');
      setError('Authentication required');
      setIsSubmitting(false);
      return;
    }
    
    try {
      const unitUpper = (newFactor.unit || 'NONE').toUpperCase();
      const activityId = newFactor.name
        ? newFactor.name.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
        : `custom-factor-${Date.now()}`;

      const factorData = {
        activityId,
        name: newFactor.name || '',
        description: '',
        category: 'TRANSPORT',
        subcategory: 'ROAD',
        activityUnit: unitUpper || 'NONE',
        emissionUnit: newFactor.unit ? `kg/${newFactor.unit}` : 'kg/NONE',
        coefficient: Number.isFinite(parseFloat(newFactor.kgCO2e)) ? parseFloat(newFactor.kgCO2e) : 0,
        country: newFactor.region || 'GLOBAL',
        region: null,
        source: 'USER_DEFINED',
        sourceVersion: null,
        fuelType: null,
        technology: null,
        validFrom: newFactor.date ? `${newFactor.date}T00:00:00Z` : new Date().toISOString(),
        validTo: null,
        notes: null,
        uncertaintyPercentage: 0
      };

      if (editingId) {
        // Update existing factor - send full payload (merge UI + existing)
        const existing = emissionFactors.find(f => f.id === editingId) || {};
        const unitUpper = (newFactor.unit || existing.activityUnit || 'NONE').toUpperCase();
        const activityId = existing.activityId || (newFactor.name
          ? newFactor.name.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
          : `custom-factor-${Date.now()}`);

        const updatePayload = {
          activityId,
          name: newFactor.name || existing.name || '',
          description: existing.description ?? '',
          category: existing.category ?? 'TRANSPORT',
          subcategory: existing.subcategory ?? 'ROAD',
          activityUnit: unitUpper || existing.activityUnit || 'NONE',
          emissionUnit: newFactor.unit ? `kg/${newFactor.unit}` : (existing.emissionUnit || 'kg/NONE'),
          coefficient: Number.isFinite(parseFloat(newFactor.kgCO2e)) ? parseFloat(newFactor.kgCO2e) : (existing.coefficient || 0),
          country: newFactor.region || existing.country || 'GLOBAL',
          region: existing.region ?? null,
          source: existing.source || 'USER_DEFINED',
          sourceVersion: existing.sourceVersion ?? null,
          fuelType: existing.fuelType ?? null,
          technology: existing.technology ?? null,
          validFrom: newFactor.date ? `${newFactor.date}T00:00:00Z` : (existing.validFrom || new Date().toISOString()),
          validTo: existing.validTo ?? null,
          notes: existing.notes ?? null,
          uncertaintyPercentage: existing.uncertaintyPercentage ?? 0,
          active: existing.active ?? true,
          version: existing.version ?? 1
        };

        const response = await fetch(`${API_URL}/api/emission-factors/${editingId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatePayload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update emission factor');
        }

        const responseData = await response.json();
        console.log('Update response:', responseData);
        
        // Refresh the list to get the latest data
        await fetchEmissionFactors();
        setEditingId(null);
      } else {
        // Add new factor
        const response = await fetch(`${API_URL}/api/emission-factors`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(factorData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to add emission factor');
        }

        const responseData = await response.json();
        console.log('Create response:', responseData);
        
        // Refresh the list to get the latest data
        await fetchEmissionFactors();
      }
      
      // Reset form and close modal
      setNewFactor({
        name: '',
        region: '',
        kgCO2e: '',
        unit: '',
        date: ''
      });
      setShowModal(false);
    } catch (err) {
      setError(err.message);
      console.error('Error saving emission factor:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setNewFactor({
      name: '',
      region: '',
      kgCO2e: '',
      unit: '',
      date: ''
    });
    setShowModal(false);
    setEditingId(null);
  };

  // Convert DD-MM-YYYY to YYYY-MM-DD for input field
  const formatDateForInput = (dateString) => {
    if (dateString && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3 && parts[0].length === 2) {
        // Format is DD-MM-YYYY, convert to YYYY-MM-DD
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    return dateString;
  };

  const handleEdit = (id) => {
    const factorToEdit = emissionFactors.find(factor => factor.id === id);
    if (factorToEdit) {
      const resolvedRegion = factorToEdit.region && factorToEdit.region.trim() !== ''
        ? factorToEdit.region
        : (factorToEdit.country || 'GLOBAL');
      setNewFactor({
        name: factorToEdit.name || '',
        region: resolvedRegion,
        kgCO2e: (factorToEdit.kgCO2e ?? factorToEdit.coefficient ?? 0).toString(),
        unit: (factorToEdit.activityUnit || factorToEdit.unit || 'NONE'),
        date: formatDateForInput(factorToEdit.date || (factorToEdit.validFrom ? String(factorToEdit.validFrom).slice(0, 10) : ''))
      });
      setShowModal(true);
      // Store the ID of the factor being edited
      setEditingId(id);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('emissionFactors.confirmations.deleteSingle'))) {
      if (!projectId) {
        setError('No project selected');
        return;
      }

      if (!user?.token) {
        console.error('No authentication token available');
        setError('Authentication required');
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/emission-factors/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete emission factor');
        }

        setEmissionFactors(prev => prev.filter(factor => factor.id !== id));
        setSelectedRows(prev => prev.filter(rowId => rowId !== id));
      } catch (err) {
        setError(err.message);
        console.error('Error deleting emission factor:', err);
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;
    
    if (!projectId) {
      setError('No project selected');
      return;
    }

    if (!user?.token) {
      console.error('No authentication token available');
      setError('Authentication required');
      return;
    }
    
    const message = selectedRows.length === 1 
      ? t('emissionFactors.confirmations.deleteSingle')
      : t('emissionFactors.confirmations.deleteMultiple', { count: selectedRows.length });
    
    if (window.confirm(message)) {
      try {
        console.log('Attempting bulk delete with IDs:', selectedRows);
        
        const response = await fetch(`${API_URL}/api/emission-factors/bulk`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ids: selectedRows }),
        });

        console.log('Bulk delete response status:', response.status);

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Bulk delete error data:', errorData);
          throw new Error(errorData.message || 'Failed to delete emission factors');
        }

        // Refresh the list to get updated data
        await fetchEmissionFactors();
        setSelectedRows([]);
        console.log('Bulk delete successful');
      } catch (err) {
        setError(err.message);
        console.error('Error deleting emission factors:', err);
      }
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil((emissionFactors?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = (emissionFactors || []).slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedRows([]); // Clear selection when changing pages
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
    setSelectedRows([]); // Clear selection
  };

  // Show message if no project is selected
  if (!projectId) {
    return (
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-12">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No project selected</h3>
          <p className="text-gray-500 mb-6">Please select a project to manage emission factors</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div>
        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('emissionFactors.title')}</h1>
          
          <div className="flex items-start justify-between">
            <p className="text-gray-600 text-sm leading-relaxed max-w-2xl">
              {t('emissionFactors.description')}
            </p>
            <button 
              onClick={() => setShowModal(true)}
              className="ml-6 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex-shrink-0"
            >
              {t('emissionFactors.addEmissionFactors')}
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
                <div className="mt-4">
                  <div className="-mx-2 -my-1.5 flex">
                    <button
                      type="button"
                      onClick={() => setError('')}
                      className="bg-red-50 px-2 py-1.5 rounded-md text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table Section */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
          {/* Bulk Actions */}
          {!loading && selectedRows.length > 0 && (
            <div className="bg-white px-6 py-3 border-b border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                {t('emissionFactors.bulkActions.selected', { count: selectedRows.length })}
              </div>
              <button
                onClick={handleDeleteSelected}
                className="px-3 py-1 text-sm font-medium text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-md transition-colors duration-200 flex items-center space-x-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>{t('emissionFactors.bulkActions.deleteSelected')}</span>
              </button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedRows.length === (emissionFactors?.length || 0)}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">
                    {t('emissionFactors.table.name')}
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">
                    {t('emissionFactors.table.region')}
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">
                    {t('emissionFactors.table.kgCO2e')}
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">
                    {t('emissionFactors.table.unit')}
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">
                    {t('emissionFactors.table.date')}
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 tracking-wider">
                    
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <>
                    {Array.from({ length: itemsPerPage }).map((_, idx) => (
                      <tr key={idx} className="animate-pulse">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="w-4 h-4 bg-gray-200 rounded-sm" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-4 bg-gray-200 rounded w-40" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-4 bg-gray-200 rounded w-28" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-4 bg-gray-200 rounded w-20" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-4 bg-gray-200 rounded w-24" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-4 bg-gray-200 rounded w-28" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex space-x-2 justify-end">
                            <div className="w-4 h-4 bg-gray-200 rounded" />
                            <div className="w-4 h-4 bg-gray-200 rounded" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                ) : currentItems.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="text-gray-400 mb-4">
                        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">{t('emissionFactors.emptyState.title')}</h3>
                      <p className="text-gray-600 mb-4">{t('emissionFactors.emptyState.description')}</p>
                      <button 
                        onClick={() => setShowModal(true)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                      >
                        {t('emissionFactors.addEmissionFactors')}
                      </button>
                    </td>
                  </tr>
                ) : (
                  currentItems.map((factor) => (
                    <tr 
                      key={factor.id} 
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(factor.id)}
                          onChange={() => handleCheckboxChange(factor.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {factor.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {factor.region}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {factor.kgCO2e}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {factor.unit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {factor.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                        <div className="flex space-x-2 justify-end">
                          <button
                            onClick={() => handleEdit(factor.id)}
                            className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                            title={t('common.edit')}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(factor.id)}
                            className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                            title={t('common.delete')}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {!loading && (
            <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="text-sm text-gray-700">
                  {t('emissionFactors.pagination.showing', { start: startIndex + 1, end: Math.min(endIndex, emissionFactors?.length || 0), total: emissionFactors?.length || 0 })}
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-700">{t('emissionFactors.pagination.show')}</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    className="text-sm bg-gray-50 border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-gray-700">{t('emissionFactors.pagination.perPage')}</span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button 
                  className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded">
                  {currentPage} {t('emissionFactors.pagination.of')} {totalPages}
                </span>
                <button 
                  className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
       </div>

       {/* Modal */}
       {showModal && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
           <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
             <div className="mt-3">
                               <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingId ? t('emissionFactors.modal.editTitle') : t('emissionFactors.modal.addTitle')}
                </h3>
               <form onSubmit={handleSubmit}>
                 <div className="mb-4">
                   <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionFactors.modal.name')}</label>
                   <input
                     type="text"
                     name="name"
                     value={newFactor.name}
                     onChange={handleInputChange}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     required
                   />
                 </div>
                                   <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionFactors.modal.region')}</label>
                    <select
                      name="region"
                      value={newFactor.region}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">{t('emissionFactors.modal.regionPlaceholder')}</option>
                      {regions.map(region => (
                        <option key={region} value={region}>{region}</option>
                      ))}
                    </select>
                  </div>
                 <div className="mb-4">
                   <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionFactors.modal.kgCO2e')}</label>
                   <input
                     type="number"
                     step="0.01"
                     name="kgCO2e"
                     value={newFactor.kgCO2e}
                     onChange={handleInputChange}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     required
                   />
                 </div>
                                   <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionFactors.modal.unit')}</label>
                    <select
                      name="unit"
                      value={newFactor.unit}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">{t('emissionFactors.modal.unitPlaceholder')}</option>
                      {units.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                  </div>
                 <div className="mb-6">
                   <label className="block text-sm font-medium text-gray-700 mb-1">{t('emissionFactors.modal.date')}</label>
                   <input
                     type="date"
                     name="date"
                     value={newFactor.date}
                     onChange={handleInputChange}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     required
                   />
                 </div>
                 <div className="flex justify-end space-x-3">
                   <button
                     type="button"
                     onClick={handleCancel}
                     className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors duration-200"
                   >
                     {t('common.cancel')}
                   </button>
                                       <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {isSubmitting && (
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      {editingId ? t('emissionFactors.modal.updateFactor') : t('emissionFactors.modal.addFactor')}
                    </button>
                 </div>
               </form>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 }

export default EmissionFactors;